import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { 
  Asset, 
  CreateAssetRequest, 
  AssignAssetRequest,
  UpdateAssetRequest,
  UpdateAssetStatusRequest, 
  AssetStatus 
} from '../types/asset.types';
import assetService from '../services/asset/asset.service';
import userService from '../services/user/user.service';
import { User } from '../types/user.types';
import { useAsync } from './useAsync';

export const useAssets = () => {
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<Asset[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<AssetStatus | string>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Assets'ları filtrelemek için useEffect
  useEffect(() => {
    if (allAssets) {
      let filtered = [...allAssets];

      // Durum filtreleme
      if (selectedStatus !== 'all') {
        filtered = filtered.filter(asset => asset.status === selectedStatus);
      }

      // Çalışan filtreleme
      if (selectedEmployee !== 'all') {
        filtered = filtered.filter(asset => asset.userId === parseInt(selectedEmployee));
      }

      // Arama terimi filtreleme
      if (searchTerm) {
        filtered = filtered.filter(asset => 
          asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.serialNumber.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setFilteredAssets(filtered);
    }
  }, [allAssets, selectedStatus, selectedEmployee, searchTerm]);

  // Tüm zimmetleri getir
  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allUserAssets = await assetService.getAllAssets();
      setAllAssets(allUserAssets);
    } catch (err: any) {
      setError(err.message || "Zimmetler yüklenirken bir hata oluştu");
      message.error("Zimmetler yüklenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  // Çalışanları getir
  const fetchEmployees = useCallback(async () => {
    try {
      const result = await userService.getUsers();
      setEmployees(result);
    } catch (err: any) {
      message.error("Çalışanlar yüklenirken bir hata oluştu");
    }
  }, []);

  // Durum değişikliği
  const handleStatusChange = (status: AssetStatus | string) => {
    setSelectedStatus(status);
  };

  // Çalışan değişikliği
  const handleEmployeeChange = (employeeId: string) => {
    setSelectedEmployee(employeeId);
  };

  // Arama terimi değişikliği
  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
  };

  // Filtreleri sıfırla
  const resetFilters = () => {
    setSelectedStatus('all');
    setSelectedEmployee('all');
    setSearchTerm('');
  };

  return {
    assets: allAssets,
    filteredAssets,
    employees,
    loading,
    error,
    selectedStatus,
    selectedEmployee,
    searchTerm,
    fetchAssets,
    fetchEmployees,
    handleStatusChange,
    handleEmployeeChange,
    handleSearchChange,
    resetFilters
  };
};

// Belirli bir kullanıcının zimmetlerini yönetmek için hook
export const useUserAssets = (userId: number) => {
  const { data: userAssets, loading, error, execute } = useAsync<Asset[]>();

  const fetchUserAssets = useCallback(async () => {
    if (userId) {
      return execute(() => assetService.getUserAssets(userId));
    }
  }, [userId, execute]);

  return {
    userAssets,
    loading,
    error,
    fetchUserAssets
  };
};

// Zimmet CRUD işlemleri için hook
export const useAssetActions = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Yeni zimmet oluştur (Envantere ekle)
  const createAsset = useCallback(async (assetData: CreateAssetRequest) => {
    setIsLoading(true);
    try {
      const result = await assetService.createAsset(assetData);
      message.success("Zimmet başarıyla envantere eklendi");
      return result;
    } catch (error: any) {
      message.error(error.message || "Zimmet oluşturulurken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Zimmeti çalışana ata
  const assignAsset = useCallback(async (assetId: number, assignData: AssignAssetRequest) => {
    setIsLoading(true);
    try {
      const result = await assetService.assignAsset(assetId, assignData);
      message.success("Zimmet başarıyla atandı");
      return result;
    } catch (error: any) {
      message.error(error.message || "Zimmet atanırken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Zimmeti geri al
  const returnAsset = useCallback(async (assetId: number) => {
    setIsLoading(true);
    try {
      const result = await assetService.returnAsset(assetId);
      message.success("Zimmet başarıyla iade edildi");
      return result;
    } catch (error: any) {
      message.error(error.message || "Zimmet iade edilirken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Zimmet bilgilerini güncelle (ad ve seri numarası)
  const updateAsset = useCallback(async (assetId: number, updateData: UpdateAssetRequest) => {
    setIsLoading(true);
    try {
      const result = await assetService.updateAsset(assetId, updateData);
      message.success("Zimmet bilgileri başarıyla güncellendi");
      return result;
    } catch (error: any) {
      message.error(error.message || "Zimmet güncellenirken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Zimmet dosyalarını güncelle
  const updateAssetFiles = useCallback(async (
    assetId: number, 
    existingFiles: { path: string; fileName: string; mimeType: string; size: number }[], 
    newFiles?: File[]
  ) => {
    setIsLoading(true);
    try {
      const result = await assetService.updateAssetFiles(assetId, existingFiles, newFiles);
      message.success("Zimmet dosyaları başarıyla güncellendi");
      return result;
    } catch (error: any) {
      message.error(error.message || "Dosyalar güncellenirken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Zimmet durumunu güncelle (örn: hurdaya ayır)
  const updateAssetStatus = useCallback(async (assetId: number, statusData: UpdateAssetStatusRequest) => {
    setIsLoading(true);
    try {
      const result = await assetService.updateAssetStatus(assetId, statusData);
      message.success("Zimmet durumu başarıyla güncellendi");
      return result;
    } catch (error: any) {
      message.error(error.message || "Zimmet durumu güncellenirken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Zimmet sil
  const deleteAsset = useCallback(async (assetId: number) => {
    setIsLoading(true);
    try {
      await assetService.deleteAsset(assetId);
      message.success("Zimmet başarıyla silindi");
    } catch (error: any) {
      message.error(error.message || "Zimmet silinirken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    createAsset,
    assignAsset,
    returnAsset,
    updateAsset,
    updateAssetFiles,
    updateAssetStatus,
    deleteAsset
  };
};



// Kullanıcının iade edilmemiş zimmetlerini kontrol etmek için hook
export const useUnreturnedAssets = () => {
  const [isLoading, setIsLoading] = useState(false);

  const checkUnreturnedAssets = useCallback(async (userId: number) => {
    setIsLoading(true);
    try {
      const result = await assetService.checkUserUnreturnedAssets(userId);
      return result;
    } catch (error: any) {
      message.error(error.message || "Zimmet kontrolü yapılırken bir hata oluştu");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    checkUnreturnedAssets
  };
}; 